import { useTranslation } from "react-i18next";
import { useContext, useState, useEffect } from "react";
import {
  ESenderRole,
  ETrasactionsType,
  IContractDetailsResponseData,
  Transactions,
} from "../../../../setup/api/services/contract/models";
import PartyTable from "../../create/partyTable";
import { ECONTRACT_STATUS, EPARTY_STATUS } from "../../../../enums";
import styles from "./styles.module.scss";
import { ContractContext } from "../../../../providers/contract";
import MBTPaymentModal from "../../status/MBTPaymentModal";
import {
  IBankTransferResponse,
  PaymentBrand,
} from "../../../../setup/api/services/payment/models";
import { makeBankTransfer } from "../../../../setup/api/services/payment";
import { PartyRow } from "../../create/partyTable";
import { getOrganization } from "../../../../setup/api/services/organization";
import { IOrganizations } from "../../../../setup/types/Organisation";

interface MainDetailsProps {
  milestone: IContractDetailsResponseData | undefined;
}

const MainDetails = ({ milestone }: MainDetailsProps) => {
  const { t } = useTranslation();
  const { contract } = useContext(ContractContext);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCustomerRow, setSelectedCustomerRow] =
    useState<PartyRow | null>(null);
  const [bankTransferResponse, setBankTransferResponse] =
    useState<IBankTransferResponse | null>(null);
  const [organization, setOrganization] = useState<IOrganizations | null>(null);

  useEffect(() => {
    if (!contract?.organizationCode) return;
    getOrganization(contract.organizationCode)
      .then((res) => setOrganization(res?.data ?? null))
      .catch(console.error);
  }, [contract?.organizationCode]);

  const showPayButton =
    contract?.status === ECONTRACT_STATUS.CREATED &&
    !!organization?.payOnBehalfAllowed;

  const arbitratorParty = contract?.parties?.find(
    (p) => p.role === ESenderRole.BROKER && p.arbitrator
  );
  const arbitratorProfile = contract?.partiesProfiles?.BROKER?.find(
    (p) => String(p.userIdentification) === String(arbitratorParty?.userId)
  );
  const arbitratorBeneficiaryId = arbitratorParty?.id?.toString() ?? "-1";
  const arbitratorBeneficiaryName =
    arbitratorProfile?.profile?.fullName ?? "WAFFY_SYSTEM";

  const handlePayClick = async (row: PartyRow) => {
    setSelectedCustomerRow(row);
    setShowPaymentModal(true);

    try {
      const response = await makeBankTransfer({
        version: contract?.version ?? 0,
        domainId: contract?.shortId ?? "",
        parentDomainId: contract?.shortId,
        domainType: "COMPLEX_CONTRACT_MILESTONE",
        paymentType: "MANUAL_BANK_TRANSFER",
        connector: "MANUAL_BANK_TRANSFER",
        paymentBrand: PaymentBrand.BANK_TRANSFER,
        currency: "SAR",
        amount: Number(row.amount),
        beneficiaryId: arbitratorBeneficiaryId,
        beneficiaryName: arbitratorBeneficiaryName,
        fromUserId: Number(row.id),
        fromUserName: row.name,
      });
      setBankTransferResponse(response);
    } catch (error) {
      console.error(error);
    }
  };

  const getPaymentMethodLabel = (
    transaction: Transactions
  ): string | undefined => {
    if (!transaction) return undefined;
    const method =
      transaction.type === "MANUAL_BANK_TRANSFER"
        ? "MANUAL_BANK_TRANSFER"
        : transaction.paymentMethod;
    if (!method) return undefined;
    switch (method) {
      case "CARD":
        return t("lbl_bank_card");
      case "APPLE_PAY":
        return t("lbl_apple_pay");
      case "STCPAY":
        return t("lbl_stc_pay");
      case "TABBY":
        return t("lbl_pay_with_tabby");
      case "TAMARA":
        return t("lbl_pay_with_tamara");
      case "MANUAL_BANK_TRANSFER":
        return t("lbl_bank_transfer");
      case "CONTRACT_TRANSFER":
        return t("lbl_contract_transfer");
      default:
        return method;
    }
  };

  const customerRows = (milestone?.partiesProfiles?.CUSTOMER || []).map(
    (party) => {
      const currentParty = milestone?.parties?.find(
        (partyObject) =>
          partyObject?.phoneNumber === party?.phoneNumber ||
          partyObject?.userId == party?.userIdentification
      );
      const paymentTransaction = milestone?.transactions?.find(
        (transaction) =>
          transaction?.fromUserId?.toString() ===
            currentParty?.userId?.toString() &&
          (transaction?.type === "CUSTOMER_PAY" ||
            transaction?.type === "MANUAL_BANK_TRANSFER") &&
          transaction?.status === "SUCCESS"
      );
      const onBehalfTransaction = milestone?.transactions?.find(
        (transaction) =>
          transaction?.fromUserId?.toString() ===
            party?.userIdentification?.toString() &&
          transaction?.paidOnBehalf === true &&
          (transaction.status === "PENDING" ||
            transaction.status === "SUCCESS") &&
          (transaction.type === ETrasactionsType.MANUAL_BANK_TRANSFER ||
            transaction.type === ETrasactionsType.CUSTOMER_PAY)
      );
      return {
        id: party?.id?.toString() || "",
        name: party?.profile?.fullName || "",
        phoneNumber: party?.phoneNumber || "",
        amount: currentParty?.amount || 0,
        status: currentParty?.status,
        type: "customer",
        fee: 0,
        total: +(currentParty?.fee || 0) + +(currentParty?.amount || 0),
        image: party?.profile?.photoUrl || "",
        paymentMethod: paymentTransaction
          ? getPaymentMethodLabel(paymentTransaction)
          : undefined,
        onBehalfByName: onBehalfTransaction?.onBehalfByName,
        hidePayButton: !!currentParty?.referenceContractId,
      };
    }
  );

  const showOnBehalfColumn = customerRows.some((row) => !!row.onBehalfByName);

  return (
    <>
      {customerRows.length > 0 && (
        <div className={styles.partyContainer}>
          <h2>{t("lbl_payees")}</h2>

          <PartyTable
            detailsPage
            rows={customerRows}
            columns={{
              showActions: false,
              showStatus: true,
              showRole: false,
              showFee: false,
              showTotal: false,
              showPaymentMethod: true,
              showOnBehalf: showOnBehalfColumn,
            }}
            showPayButton={showPayButton}
            onPayClick={handlePayClick}
            getIndexByRow={(row) =>
              (milestone?.parties || [])?.findIndex(
                (p) => p?.id?.toString() === row?.id
              )
            }
          />
        </div>
      )}

      {(milestone?.partiesProfiles?.PROVIDER || []).length > 0 && (
        <div className={styles.partyContainer}>
          <h2>{t("lbl_receivers")}</h2>

          <PartyTable
            detailsPage
            rows={(milestone?.partiesProfiles?.PROVIDER || [])?.map((party) => {
              const currentParty = milestone?.parties?.find(
                (partyObject) =>
                  partyObject?.phoneNumber === party?.phoneNumber ||
                  partyObject?.userId == party?.userIdentification
              );
              return {
                id: party?.id?.toString() || "",
                name: party?.profile?.fullName || "",
                phoneNumber: party?.phoneNumber || "",
                amount: currentParty?.amount || 0,
                status:
                  milestone?.cashOutAmountList?.find(
                    (item) => item?.userIdentification === currentParty?.userId
                  )?.settlementAmountStatus === EPARTY_STATUS.AVAILABLE_BALANCE
                    ? EPARTY_STATUS.AVAILABLE_BALANCE
                    : currentParty?.status,
                type: "provider",
                fee: 0,
                total: +(currentParty?.fee || 0) + +(currentParty?.amount || 0),
                image: party?.profile?.photoUrl || "",
              };
            })}
            columns={{
              showActions: false,
              showStatus: true,
              showRole: false,
              showFee: false,
              showTotal: false,
            }}
            getIndexByRow={(row) =>
              (milestone?.parties || [])?.findIndex(
                (p) => p?.id?.toString() === row?.id
              )
            }
          />
        </div>
      )}

      {(milestone?.partiesProfiles?.BROKER || []).length > 0 && (
        <div className={styles.partyContainer}>
          <h2>{t("lbl_brokers")}</h2>

          <PartyTable
            detailsPage
            rows={(milestone?.partiesProfiles?.BROKER || [])?.map((party) => {
              const currentParty = milestone?.parties?.find(
                (partyObject) => partyObject?.role === ESenderRole.BROKER
              );
              return {
                id: party?.id?.toString() || "",
                name: party?.profile?.fullName || "",
                phoneNumber: party?.phoneNumber || "",
                amount: currentParty?.amount || 0,
                status:
                  milestone?.cashOutAmountList?.find(
                    (item) => item?.userIdentification === currentParty?.userId
                  )?.settlementAmountStatus === EPARTY_STATUS.AVAILABLE_BALANCE
                    ? EPARTY_STATUS.AVAILABLE_BALANCE
                    : currentParty?.status,
                type: "provider",
                fee: 0,
                total: +(currentParty?.fee || 0) + +(currentParty?.amount || 0),
                image: party?.profile?.photoUrl || "",
              };
            })}
            columns={{
              showActions: false,
              showStatus: true,
              showRole: false,
              showFee: false,
              showTotal: false,
            }}
            getIndexByRow={(row) =>
              (milestone?.parties || [])?.findIndex(
                (p) => p?.id?.toString() === row?.id
              )
            }
          />
        </div>
      )}
      <MBTPaymentModal
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        customerAmount={
          selectedCustomerRow ? Number(selectedCustomerRow.amount) : undefined
        }
        contract={contract ?? undefined}
        customerRow={selectedCustomerRow ?? undefined}
        beneficiaryId={arbitratorBeneficiaryId}
        beneficiaryName={arbitratorBeneficiaryName}
      />
    </>
  );
};

export default MainDetails;
